import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdPerson } from "react-icons/io";
import { TbLogout } from "react-icons/tb";
import { HiGlobeAmericas } from "react-icons/hi2";
import { FaPhone } from "react-icons/fa";
import { FaTrashAlt } from "react-icons/fa";
import { useAuth } from '../AuthContext';
import { userService } from '../services/api';
import { useGoogleMapsApi } from '../hooks/useGoogleMapsApi';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    city: '',
    state: '',
    new_password: '',
    phone_number: '',
  });
  const [locationInput, setLocationInput] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const autocompleteRef = useRef(null);
  const isGoogleMapsLoaded = useGoogleMapsApi();

  useEffect(() => {
    if (user) {
      setFormData(prevState => ({
        ...prevState,
        first_name: user.first_name,
        city: user.city,
        state: user.state,
        phone_number: user.phone_number,
      }));
      setLocationInput(`${user.city}, ${user.state}`);
    }
  }, [user]);

  useEffect(() => {
    if (isGoogleMapsLoaded) {
      initAutocomplete();
    }
  }, [isGoogleMapsLoaded]);

  const initAutocomplete = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        document.getElementById('location-input'),
        {
          types: ['(cities)'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'geometry', 'name'],
        }
      );

      autocompleteRef.current.addListener('place_changed', handlePlaceSelect);

      // Prevent form submission on Enter key press
      document.getElementById('location-input').addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      });

      // Handle Enter key to select the first suggestion
      document.getElementById('location-input').addEventListener('keyup', (e) => {
        if (e.key === 'Enter') {
          const firstResult = document.querySelector('.pac-item');
          if (firstResult) {
            const event = new Event('click');
            firstResult.dispatchEvent(event);
          }
        }
      });
    }
  };

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.address_components) {
      let city = '';
      let state = '';

      for (const component of place.address_components) {
        if (component.types.includes('locality')) {
          city = component.long_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }
      }

      setLocationInput(`${city}, ${state}`);
      setFormData(prevState => ({
        ...prevState,
        city: city,
        state: state
      }));
    }
  };

  const handleLocationInputChange = (e) => {
    setLocationInput(e.target.value);
    // Parse city and state from manual input
    const [city, state] = e.target.value.split(',').map(item => item.trim());
    setFormData(prevState => ({
      ...prevState,
      city: city || '',
      state: state || ''
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let dataToUpdate = {
        first_name: formData.first_name,
        city: formData.city,
        state: formData.state,
      };

      if (formData.new_password) {
        dataToUpdate.password = formData.new_password;
      }

      const response = await userService.updateUser(user.id, dataToUpdate);
      
      // Clear password fields
      setFormData(prevState => ({
        ...prevState,
        new_password: ''
      }));

      // Update the user context with the new data
      login(response.data);

      navigate('/profile');

    } catch (error) {
      console.error('Profile update failed:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleDeleteAccount = async () => {
    if (!user || !user.id) return;
    
    try {
      setIsDeleting(true);
      setDeleteError('');
      
      // Delete the user account
      await userService.deleteUser(user.id);
      
      // Log out the user
      logout();
      
      // Redirect to login page
      navigate('/login');
    } catch (error) {
      console.error('Account deletion failed:', error);
      setDeleteError('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen w-full bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/images/leelo_landing.jpg')" }}>
      <div className="w-full max-w-7xl mx-auto p-5 flex flex-col items-center justify-center">
      <header className="flex justify-end items-center mb-5 px-5 w-full">
        <div className="flex items-center gap-7">
          <span className="flex items-center gap-2 text-white">
            {user.city}, {user.state} <HiGlobeAmericas className="text-lg" />
          </span>
          <button 
            onClick={() => navigate('/profile')} 
            className="flex items-center gap-1 font-bold text-white hover:text-tropical-indigo cursor-pointer flex-row-reverse"
          >
            <IoMdPerson className="text-lg" /> View Profile
          </button>
        </div>
      </header>
      
      <main className="flex-grow bg-gradient-to-br from-white/95 to-uranian-blue/80 backdrop-blur-sm shadow-xl rounded-lg py-16 px-12 w-full max-w-2xl mx-auto border border-white/20">
        <h1 className="text-2xl font-bold text-center mb-2 text-tropical-indigo">Edit Your Profile</h1>
        <p className="text-dark-gray text-center mb-6 text-sm">You can update your details.</p>
        
        <form onSubmit={handleSubmit} className="w-full max-w-md mx-auto">
          <div className="mb-4">
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="First Name"
              required
              className="w-full p-3 border border-white/30 rounded bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
            />
          </div>
          
          <div className="mb-4">
            <input
              id="location-input"
              type="text"
              value={locationInput}
              onChange={handleLocationInputChange}
              placeholder="Enter your city and state"
              required
              className="w-full p-3 border border-white/30 rounded bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
            />
          </div>
          
          <div className="mb-4">
            <input
              type="password"
              name="new_password"
              value={formData.new_password}
              onChange={handleChange}
              placeholder="New Password"
              className="w-full p-3 border border-white/30 rounded bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
            />
          </div>
          
          <div className="flex justify-between gap-4 mt-6">
            <button 
              type="button" 
              onClick={() => navigate('/profile')} 
              className="w-1/2 py-3 bg-white text-black border border-black rounded hover:text-tropical-indigo hover:border-tropical-indigo font-bold"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="w-1/2 py-3 bg-tropical-indigo text-white border-none rounded hover:opacity-90 font-bold"
            >
              Save Profile
            </button>
          </div>
          
          <div className="flex justify-center mt-8">
            <button 
              type="button" 
              onClick={() => setShowDeleteModal(true)}
              className="text-red-600 flex items-center gap-2 text-sm hover:underline"
            >
              <FaTrashAlt /> Delete My Account
            </button>
          </div>
        </form>
      </main>
      
      <footer className="flex justify-between items-center py-6 px-8 w-full max-w-2xl mx-auto">
        <p className="text-xs text-white m-0 flex items-center">2025 © Wenzi, Inc</p>
        <button 
          onClick={handleLogout} 
          className="bg-transparent border-none cursor-pointer font-bold text-white hover:text-tropical-indigo flex items-center gap-2 flex-row-reverse p-0 m-0"
        >
          <TbLogout className="text-lg" /> Log Out
        </button>
      </footer>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-gradient-to-br from-white/95 to-uranian-blue/80 backdrop-blur-sm p-6 rounded-lg w-11/12 max-w-md text-center border border-white/20 shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-tropical-indigo">Delete Account</h2>
            <p className="text-gray-600 mb-6 text-sm">
              Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data, including your Wenzis.
            </p>
            {deleteError && <p className="text-red-600 mb-4">{deleteError}</p>}
            <div className="flex justify-center gap-4">
              <button 
                type="button" 
                onClick={() => setShowDeleteModal(false)} 
                disabled={isDeleting}
                className="px-6 py-2 bg-white border border-black rounded hover:text-tropical-indigo hover:border-tropical-indigo font-bold w-32"
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={handleDeleteAccount} 
                disabled={isDeleting}
                className="px-6 py-2 bg-red-600 text-white border-none rounded hover:opacity-90 font-bold w-32"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default EditProfile;
